/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react'
import { useRequest } from '../../hooks/UseRequest'
import Loading from '../../components/Loading'
import DashletSuccessIcon from '../../components/elements/DashletSuccessIcon'
import DashletDeclineIcon from '../../components/elements/DashletDeclineIcon'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { selectModules } from '../../redux/moduleSlice'
import DashletTooltip from '../../components/elements/DashletTooltip'
import NoRecord from '../../components/elements/NoRecord'
import ViewSensitivePiiButtons from '../../components/ViewSensitivePiiButtons'
import { selectSubpanelRerender } from "../../redux/rerenderSlice";
import { FormatISODate } from '../../helpers/formatting'
import { setModal } from '../../redux/modalSlice'
import { selectUser } from '../../redux/userSlice'
import { hasUserPermission } from '../../utils/permissionHelper'

export default function CustomerBankAccountDashlet({ endpointPath }) {
  const dispatch = useDispatch()
  const requests = useRequest()
  const requesting = useRef(false)
  const [row, setRow] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [module, setModule] = useState()
  const modules = useSelector(selectModules)
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, 'bank_change_dashlet'));
  const active = useSelector(selectUser)
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
  const getRow = async () => {
    requests.setEndpoint(endpointPath)
    try {
      const response = await requests.get()
      setRow(response.data.data)

      const m = modules.data.find((e) => e.code_key === 'bank_account')
      setModule(m)
      requesting.current = false
      setLoaded(true)
    } catch (error) {
      setLoaded(true)
      requesting.current = false
    }
  }

  useEffect(() => {
    if (!requesting.current && endpointPath && (row.length === 0 || rerenderValue)) {
      setLoaded(false)
      requesting.current = true
      getRow()
    }
  }, [endpointPath, rerenderValue])

  const renderHeader = (column_name, customize_label = false) => {
    if (customize_label) {
      return column_name;
    }
    const data = module.module_fields.find(
      (header) => header.column_name === column_name
    )
    if (data && !data.helper_text) {
      return data?.label_name
    }
    return <DashletTooltip columnName={data?.label_name} tooltipText={data?.helper_text} />
  }
  return (
    <div className="message is-white lox-dashlet-detail">
      <div className="message-header has-background-primary has-text-white lox-dashlet-detail__title py-0">
        <p className="is-size-4">Primary Bank Account</p>
      </div>
      <div className="message-body py-0 px-1" style={{ lineHeight: 1.40 }}>
        {loaded && row && row.id && (
          <>
            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('id')}
              </p>
              <p className="lox-dashlet-detail__item__data has-text-link is-size-8">
                <Link
                  key={`customer-bank-account-dashlet-view-${row.id}`}
                  to={`/bank_account/${row.id}`}
                  className="lox-has-text-decoration-none"
                >
                  {row.id}
                </Link>
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('bank_account_holder_name')}
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                {row.bank_account_holder_name || '-'}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('bank_name')}
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                {row.bank_name || '-'}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('bank_routing_number_id')}
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                {row.bank_routing_number || '-'}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('bank_account_number')}
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                {'#'.repeat(6) + row.bank_account_number_last_four || '-'}
                <ViewSensitivePiiButtons
                  customerId={row.customer_id}
                  type={'full_bank_account_number'}
                  style={{ height: '20px' }}
                />
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('is_bank_savings')}
              </p>
              <p className="lox-dashlet-detail__item__data is-size-8 px-0 lox-dashlet-detail__icon">
                {row.is_bank_savings !== 1 ? (
                  <DashletDeclineIcon />
                ) : (
                  <DashletSuccessIcon />
                )}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('is_bank_account_verified')}
              </p>
              <p className="lox-dashlet-detail__item__data is-size-8 px-0 lox-dashlet-detail__icon">
                {row.is_bank_account_verified !== 1 ? (
                  <DashletDeclineIcon />
                ) : (
                  <DashletSuccessIcon />
                )}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>
            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('is_bypassed_verification')}
              </p>
              <p className="lox-dashlet-detail__item__data is-size-8 px-0 lox-dashlet-detail__icon">
                {row.is_bypassed_verification !== 1 ? (
                  <DashletDeclineIcon />
                ) : (
                  <DashletSuccessIcon />
                )}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            <div className="lox-dashlet-detail__item py-0 px-1">
              <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
                {renderHeader('last_successful_payment_id')}
              </p>
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                {row?.last_successful_payment_created_at ? FormatISODate(row.last_successful_payment_created_at) : '-'}
              </p>
            </div>
            <div className="divider m-0 mx-1"></div>

            {hasUserPermission(activePermissions, 'bank_transaction_provider_call_create') && (
              <div className="lox-dashlet-detail__item py-0 px-1">
                <button
                  type="button"
                  className="button is-link is-small is-fullwidth"
                  onClick={() => {
                    dispatch(setModal({
                      is_active: true,
                      type: 'bank_logins_modal',
                      options: { customerId: row.customer_id }
                    }))
                  }}
                >
                  View Bank Logins
                </button>
              </div>
            )}
          </>
        )}
        {!loaded && <Loading />}
        {loaded && row.length === 0 && <NoRecord />}
      </div>
    </div>
  )
}
